import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Info, Loader2, LockKeyhole, Mail } from 'lucide-react';
import { GOOGLE_LOGIN_ERRORS, loginSchema, type GoogleLoginError, type LoginInput, type SessionDto } from '@orienta/shared';
import { api, applyFieldErrors, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/form';
import { safeNext } from '@/lib/navigation';
import { WelcomeInput, WelcomeLayout, welcomeSubmitClass } from '../WelcomeLayout';
import { useSession } from '../session';
import { useAuthConfig } from '../api';
import { GoogleButton, OrDivider } from '../GoogleButton';

export function LoginPage() {
  const { session, setSession } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const config = useAuthConfig();
  const googleError = params.get('google');
  const [formError, setFormError] = useState<string | null>(googleError ? (GOOGLE_LOGIN_ERRORS[googleError as GoogleLoginError] ?? GOOGLE_LOGIN_ERRORS.falhou) : null);
  const form = useForm<LoginInput>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '' } });
  const { errors, isSubmitting } = form.formState;
  const next = safeNext(params.get('next'));

  if (session) return <Navigate to={next} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const s = await api<SessionDto>('/auth/login', { method: 'POST', body: values, skipAuthRedirect: true });
      setSession(s);
      navigate(next, { replace: true });
    } catch (err) {
      if (!applyFieldErrors(err, form.setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <WelcomeLayout title="Entrar" description="Bom ter você de volta.">
      <form onSubmit={onSubmit} noValidate className="grid gap-4">
        <FormError message={formError} />
        {config.data?.google && (
          <>
            <div className="grid gap-2">
              <GoogleButton next={next} className="h-12 rounded-xl text-base font-semibold" />
              <p className="text-muted-foreground text-center text-xs">Primeiro acesso? Sua conta de orientador(a) é criada na hora.</p>
            </div>
            <OrDivider label="ou com e-mail e senha" className="tracking-wide uppercase" />
          </>
        )}
        <Field label="E-mail" error={errors.email?.message} required>
          {(p) => <WelcomeInput icon={Mail} type="email" autoComplete="email" autoFocus={!googleError} placeholder="voce@universidade.br" {...p} {...form.register('email')} />}
        </Field>
        <div className="grid gap-1.5">
          <Field label="Senha" error={errors.password?.message} required>
            {(p) => <WelcomeInput icon={LockKeyhole} type="password" autoComplete="current-password" {...p} {...form.register('password')} />}
          </Field>
          <Link to="/esqueci-senha" className="text-primary justify-self-end text-sm font-medium underline-offset-4 hover:underline">
            Esqueci minha senha
          </Link>
        </div>
        <Button type="submit" size="lg" className={welcomeSubmitClass} disabled={isSubmitting}>
          Entrar
          {isSubmitting ? <Loader2 className="animate-spin" /> : <ArrowRight />}
        </Button>
        <div className="mt-2 border-t pt-5 text-center text-sm">
          <span className="text-muted-foreground">É orientador(a)? </span>
          <Link to="/cadastro" className="text-primary font-semibold underline-offset-4 hover:underline">
            Crie sua conta
          </Link>
        </div>
        <p className="bg-muted/50 flex gap-2.5 rounded-xl border p-4 text-sm">
          <Info className="text-info mt-0.5 size-4 shrink-0" aria-hidden />
          <span className="text-muted-foreground">
            <strong className="text-foreground font-medium">É orientando(a)?</strong> Você entra pelo convite ou pelo link de ingresso enviado pela sua orientação.
          </span>
        </p>
      </form>
    </WelcomeLayout>
  );
}
